/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';
import { notifications } from '@mantine/notifications';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { IApiError } from '@global-types/index';
import { useParams } from 'react-router';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TApiBody = {
	site: string;
	serialNumber: string;
	location: {
		countryCode: string;
		place: string;
	};
};

type TApiResponse = {
	site: string;
	project: string;
	organization: string;
	entity: string;
	ratio: number;
	startDate: string;
};

type TMeterPoint = {
	serialNumber: string;
	location: {
		countryCode: string;
		place: string;
	};
};
type TBody = {
	site: string;
	meterPoints: TMeterPoint[];
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const addMeterPoint = (
	data: TApiBody
): Promise<{
	response: TApiResponse;
}> => {
	return apiClient.post('/meter_points', data);
};

export const useAddMeterPoint = (
	page: number,
	size: number
) => {
	const { t } = useTranslation();
	const { projectId } = useParams();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: async (formData: TBody) => {
			const { meterPoints, site } = formData;

			await Promise.all(
				meterPoints.map((_meterPoint: TMeterPoint) => {
					const customBody = {
						site,
						serialNumber: _meterPoint.serialNumber,
						location: {
							countryCode:
								_meterPoint.location.countryCode,
							place: _meterPoint.location.place,
						},
					};

					return addMeterPoint(customBody);
				})
			);
		},

		onSuccess: async () => {
			notifications.show({
				title: t('global.success'),
				message: t(
					'meterPoints.notifications.meterPointSuccessfullyAdded'
				),
				color: 'success',
			});
			if (projectId) {
				queryClient.invalidateQueries({
					queryKey: ['measurability-projects', projectId],
				});
			}

			queryClient.invalidateQueries({
				queryKey: [
					'meter-points-projects',
					{ page: page, size: size },
				],
			});
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
